/**
 * Submit hook for `DischargeInteractionForm`. Thin wrapper around
 * `useEncounterUpsertSubmit` with the `discharge-interaction`
 * discriminator and the `"discharge"` notification copy variant.
 *
 * @agentic connections
 */

import type { Encounter, Patient, PractitionerRole } from "@medplum/fhirtypes";
import type {
  DischargeInteractionFormData,
  EncounterActivityContext,
} from "@benchmark/core/types";

import { useEncounterUpsertSubmit } from "./useEncounterUpsertSubmit";

/**
 * Options for {@link useDischargeInteractionSubmit}.
 *
 * @agentic connections
 */
export type UseDischargeInteractionSubmitOptions = {
  patient: Patient | undefined;
  existingEncounter?: Encounter;
  practitionerRoles?: PractitionerRole[];
  activityContext?: EncounterActivityContext;
  onSuccess?: (encounterId: string) => void;
  onSubmitted?: () => void;
  getCareTimeMinutes?: () => number | undefined;
  getCareTimeCommunicationId?: () => string | undefined;
  getCareTimeEffectivePeriod?: () => { start: string; end: string } | undefined;
};

/**
 * Returns a submit handler for `DischargeInteractionForm`.
 *
 * @agentic connections
 */
export const useDischargeInteractionSubmit = (
  options: UseDischargeInteractionSubmitOptions,
): ((formData: DischargeInteractionFormData) => Promise<void>) =>
  useEncounterUpsertSubmit<"discharge-interaction">({
    type: "discharge-interaction",
    successCopyVariant: "discharge",
    careTimeMethod: "task-completion",
    ...options,
  });
